import { Injectable, Signal, computed, signal } from '@angular/core';
import type { ClientBlockStatus } from '@app/core/models';

/**
 * CB — Client Blocking (2026-06-30, AD-049 / CB-LD-7).
 *
 * Signal-based bridge between block-detecting code (route guards, the
 * sidebar menu interceptor, the error interceptor) and the
 * `BlockedConsentModalComponent` rendered inside
 * `ClientLayoutComponent`.
 *
 * Guards and interceptor functions cannot directly open a component —
 * this service holds the desired modal state in a signal and the layout
 * subscribes to it. State lives in memory only (no localStorage /
 * sessionStorage / IndexedDB writes — see `.claude/rules/security.md`).
 *
 * The level `'none'` is intentionally NOT openable — `open(...)`'s
 * parameter is `Exclude<ClientBlockStatus, 'none'>` so callers cannot
 * accidentally show an empty modal for an unblocked user. The
 * fully-`blocked` login-time modal is rendered directly by
 * `LoginComponent` with its own local signals (CB-LD-13).
 */
export type BlockLevel = Exclude<ClientBlockStatus, 'none'>;

@Injectable({ providedIn: 'root' })
export class BlockModalService {
  private readonly _level = signal<BlockLevel | null>(null);
  private readonly _reason = signal<string | null>(null);

  /** The active block level, or `null` when the modal is closed. */
  readonly level: Signal<BlockLevel | null> = this._level.asReadonly();
  /** The admin-supplied block reason, or `null` when none was captured. */
  readonly reason: Signal<string | null> = this._reason.asReadonly();
  /** Convenience — true when a level is set. Drives the modal's `isOpen`. */
  readonly isOpen: Signal<boolean> = computed(() => this._level() !== null);

  /**
   * Open the consent modal for a given block level + reason. Idempotent
   * in effect — calling twice with the same level just refreshes the
   * stored reason; the modal stays visible.
   */
  open(level: BlockLevel, reason: string | null): void {
    this._level.set(level);
    this._reason.set(reason);
  }

  /** Hide the modal and clear the captured reason. */
  close(): void {
    this._level.set(null);
    this._reason.set(null);
  }
}
